import { api } from '../api/endpoints.js';
import { haptic } from '../tg.js';

// Airdrop bottom sheet — contribution score + tier + token preview + breakdown.
//
// Shape from GET /v1/airdrop/me:
//   { contribution_score, tier, next_tier, next_tier_at, projected_tokens,
//     distribution_at, breakdown: {
//       account_age_days, account_age_score, topup_total, topup_score,
//       play_sessions, play_score, high_score, high_score_score,
//       referrals_count, referrals_score } }

const TIER_THEME = {
  BRONZE:   { emoji: '🟤', class: 'tier-bronze',   ring: '#a0673a' },
  SILVER:   { emoji: '⚪', class: 'tier-silver',   ring: '#cfd6e0' },
  GOLD:     { emoji: '🟡', class: 'tier-gold',     ring: '#f5c542' },
  PLATINUM: { emoji: '💠', class: 'tier-platinum', ring: '#5cd6ff' },
  DIAMOND:  { emoji: '💎', class: 'tier-diamond',  ring: '#ff2bb1' }
};

export function openAirdropSheet(parent) {
  return new Promise((resolve) => {
    const el = document.createElement('div');
    el.className = 'modal-overlay sheet-overlay';
    el.innerHTML = `
      <div class="sheet sheet-airdrop">
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <div class="sheet-title">AIRDROP</div>
          <div class="sheet-sub">Your contribution decides your share</div>
        </div>
        <div class="sheet-body" id="ad-body">
          <div class="sheet-loading">Loading…</div>
        </div>
        <button class="sheet-close">Close</button>
      </div>`;
    parent.appendChild(el);

    const sheetEl = el.querySelector('.sheet');
    requestAnimationFrame(() => sheetEl.classList.add('show'));

    const close = () => {
      if (sheetEl.classList.contains('closing')) return;
      sheetEl.classList.add('closing');
      setTimeout(() => { el.remove(); resolve({ status: 'closed' }); }, 220);
    };
    el.querySelector('.sheet-close').onclick = close;
    el.onclick = (ev) => { if (ev.target === el) close(); };

    const body = el.querySelector('#ad-body');

    api.getAirdropInfo().then((d) => {
      const theme = TIER_THEME[d.tier] || TIER_THEME.BRONZE;
      const b = d.breakdown || {};
      const nextTierPct = d.next_tier_at > 0
        ? Math.max(5, Math.min(100, Math.round(100 * (d.contribution_score / (d.contribution_score + d.next_tier_at)))))
        : 100;

      body.innerHTML = `
        <div class="ad-hero ${theme.class}">
          <div class="ad-tier-ring">
            <div class="ad-tier-emoji">${theme.emoji}</div>
          </div>
          <div class="ad-tier-name">${d.tier}</div>
          <div class="ad-score-block">
            <div class="ad-score-label">CONTRIBUTION</div>
            <div class="ad-score-val">${formatNum(d.contribution_score)}</div>
          </div>
          ${d.next_tier ? `
            <div class="ad-next">
              <div class="ad-next-bar"><div class="ad-next-fill" style="width:${nextTierPct}%"></div></div>
              <div class="ad-next-label">+${formatNum(d.next_tier_at)} to <b>${d.next_tier}</b></div>
            </div>
          ` : `<div class="ad-next-label">MAX TIER · keep contributing</div>`}
        </div>

        <div class="ad-tokens">
          <div class="ad-tokens-l">EST. ALLOCATION</div>
          <div class="ad-tokens-v neon-gold">≈ ${formatTokens(d.projected_tokens)}</div>
          <div class="ad-tokens-meta">Distribution: <b>${d.distribution_at || 'TBD'}</b> · preview only, final TBD</div>
        </div>

        <div class="ad-breakdown">
          <div class="ad-breakdown-header">SCORE BREAKDOWN</div>
          ${row('⏳', 'Account age',     b.account_age_days  + ' days',           b.account_age_score)}
          ${row('💎', 'Stars deposited', b.topup_total       + '⭐ topped up',     b.topup_score)}
          ${row('🎮', 'Games played',    b.play_sessions     + ' sessions',       b.play_score)}
          ${row('🏆', 'High score',      formatNum(b.high_score),                 b.high_score_score)}
          ${row('💌', 'Friends invited', b.referrals_count   + ' invited',        b.referrals_score)}
        </div>

        <div class="ad-tips">
          <div class="ad-tips-header">BOOST YOUR SCORE</div>
          <div class="ad-tip">⭐ Top up — biggest weight (×5 per star)</div>
          <div class="ad-tip">💌 Invite friends — +100 per friend</div>
          <div class="ad-tip">🎮 Play daily — +10 per finished game</div>
        </div>
      `;
    }).catch((e) => {
      haptic('error');
      body.innerHTML = `<div class="sheet-error">${e?.message || 'Failed to load'}</div>`;
    });
  });
}

function row(icon, label, raw, score) {
  return `
    <div class="ad-row">
      <span class="ad-row-icon">${icon}</span>
      <div class="ad-row-meta">
        <div class="ad-row-label">${label}</div>
        <div class="ad-row-raw">${raw}</div>
      </div>
      <div class="ad-row-score">+${formatNum(score)}</div>
    </div>`;
}

function formatNum(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '0';
  return n.toLocaleString();
}

function formatTokens(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toFixed(1).replace(/\.0$/, '');
}
